// Sample-based SFX (v1 effects-manager audio half; same .wav files from
// assets/sound/). Buffers decode lazily on first unlock.

const SAMPLES = {
  jump: 'assets/sound/jump.wav',
  carPass: 'assets/sound/car1.wav',
  enemyAttack: 'assets/sound/attack.wav',
  enemyDefeat: 'assets/sound/pop.wav',
  coinCollect: 'assets/sound/coin.wav',
  gameOver: 'assets/sound/gameover.wav',
  gameStart: 'assets/sound/gamestart.wav',
  carHonk: 'assets/sound/horn.wav',
  combat: 'assets/sound/combat.wav',
  crash: 'assets/sound/crash.wav',
} as const;

export type SfxName = keyof typeof SAMPLES;

export class SfxPlayer {
  private context: AudioContext | null = null;
  private readonly buffers = new Map<SfxName, AudioBuffer>();
  private masterVolume = 0.6;

  /** Call from a user gesture; creates the context and decodes samples. */
  async unlock(): Promise<void> {
    if (this.context) {
      if (this.context.state === 'suspended') await this.context.resume();
      return;
    }
    this.context = new AudioContext();
    await Promise.all(
      (Object.entries(SAMPLES) as [SfxName, string][]).map(async ([name, url]) => {
        try {
          const response = await fetch(url);
          const data = await response.arrayBuffer();
          const buffer = await (this.context as AudioContext).decodeAudioData(data);
          this.buffers.set(name, buffer);
        } catch {
          // Missing/undecodable sample — that sound stays silent
        }
      }),
    );
  }

  play(name: SfxName, volume = 1.0): void {
    const context = this.context;
    const buffer = this.buffers.get(name);
    if (!context || !buffer || context.state !== 'running') return;
    const source = context.createBufferSource();
    source.buffer = buffer;
    const gain = context.createGain();
    gain.gain.value = this.masterVolume * volume;
    source.connect(gain);
    gain.connect(context.destination);
    source.start();
  }
}
